import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon, SparklesIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/solid';
import {
  IChatMessage,
  ChatContextType,
  ChatMessageRole,
  LLMStreamEventType,
  SettingAIResponse
} from '@packages/global';
import { chatMessageApi, llmApi } from '../../services/user';
import { useToast } from '../../stores/toastStore';
import { useDialog } from '../../hooks/useDialog';

interface SettingChatPanelProps {
  projectId: string;
  contextType: ChatContextType;
  currentContent?: string;
  onApply: (content: string) => void;
}

export default function SettingChatPanel({
  projectId,
  contextType,
  currentContent,
  onApply
}: SettingChatPanelProps) {
  const { t } = useTranslation(['projectSettings', 'common']);
  const toast = useToast();
  const { confirm } = useDialog();
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadMessages();
    return () => { abortControllerRef.current?.abort(); };
  }, [projectId, contextType]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages, streamingContent]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await chatMessageApi.list({ projectId, contextType, limit: 50 });
      setMessages(data);
    } catch (error) {
      console.error('加载消息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || streaming) return;
    const userMessage = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setStreaming(true);
    setStreamingContent('');

    const tempUserMsg: IChatMessage = {
      id: `temp-${Date.now()}`, projectId, contextType,
      role: ChatMessageRole.USER, content: userMessage,
      createdAt: new Date(), updatedAt: new Date()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      abortControllerRef.current = new AbortController();
      await llmApi.stream(
        { projectId, contextType, prompt: userMessage, currentContent },
        (event) => {
          switch (event.type) {
            case LLMStreamEventType.DELTA:
              setStreamingContent(prev => prev + (event.content || ''));
              break;
            case LLMStreamEventType.DONE:
              const aiMsg: IChatMessage = {
                id: `ai-${Date.now()}`, projectId, contextType,
                role: ChatMessageRole.ASSISTANT, content: event.content || '',
                createdAt: new Date(), updatedAt: new Date()
              };
              setMessages(prev => [...prev, aiMsg]);
              setStreamingContent('');
              setStreaming(false);
              break;
            case LLMStreamEventType.ERROR:
              toast.error(event.error || t('subjectChat.aiGenerateFailed'));
              setStreaming(false);
              setStreamingContent('');
              break;
          }
        },
        abortControllerRef.current.signal
      );
    } catch (error: any) {
      if (error.name !== 'AbortError') toast.error(t('subjectChat.sendFailed'));
      setStreaming(false);
      setStreamingContent('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = async () => {
    const confirmed = await confirm(t('subjectChat.clearConfirm'), t('subjectChat.clearConfirmTitle'), 'warning');
    if (!confirmed) return;
    try {
      await chatMessageApi.clear(projectId, contextType);
      setMessages([]);
      toast.success(t('subjectChat.chatCleared'));
    } catch { toast.error(t('subjectChat.clearFailed')); }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (messageId.startsWith('temp-') || messageId.startsWith('ai-')) {
      setMessages(prev => prev.filter(m => m.id !== messageId));
      return;
    }
    try {
      await chatMessageApi.delete(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch { toast.error(t('subjectChat.deleteFailed')); }
  };

  const handleApplyContent = (content: string) => {
    onApply(content);
    toast.success(t('settingChat.contentApplied'));
  };

  const formatTime = (date: Date) => {
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const parseMessageContent = (content: string): { message: string; settingContent: string | null } => {
    try {
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[1]) as SettingAIResponse;
        return { message: parsed.message, settingContent: parsed.content || null };
      }
      const trimmed = content.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const parsed = JSON.parse(trimmed) as SettingAIResponse;
        return { message: parsed.message, settingContent: parsed.content || null };
      }
      const jsonObjectMatch = content.match(/\{[\s\S]*"message"[\s\S]*"content"[\s\S]*\}/);
      if (jsonObjectMatch) {
        const parsed = JSON.parse(jsonObjectMatch[0]) as SettingAIResponse;
        return { message: parsed.message, settingContent: parsed.content || null };
      }
      return { message: content, settingContent: null };
    } catch {
      return { message: content, settingContent: null };
    }
  };

  const getContextLabel = () => {
    switch (contextType) {
      case ChatContextType.STORY_SYNOPSIS: return t('settingChat.context.storySynopsis');
      case ChatContextType.WORLDVIEW: return t('settingChat.context.worldview');
      case ChatContextType.WRITING_STYLE: return t('settingChat.context.writingStyle');
      default: return t('settingChat.context.default');
    }
  };

  return (
    <div className="h-full flex flex-col bg-bg-page rounded-xl border border-border-subtle overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <SparklesIcon className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-50">{t('settingChat.title', { context: getContextLabel() })}</span>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button onClick={handleClear} className="p-1.5 text-gray-400 hover:text-red-400 transition-colors" title={t('subjectChat.clearChat')}>
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-1.5 px-2 py-1 bg-green-900/30 rounded">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
            <span className="text-[11px] text-green-500 font-medium">{t('subjectChat.online')}</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400">
            <span className="text-sm">{t('subjectChat.loading')}</span>
          </div>
        ) : (
          <>
            {/* 欢迎消息 */}
            <div className="flex gap-2.5">
              <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                <SparklesIcon className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                <p className="text-[13px] text-gray-200 leading-relaxed">
                  {t('settingChat.welcome', { context: getContextLabel() })}
                </p>
              </div>
            </div>

            {messages.map((msg) => {
              const isUser = msg.role === ChatMessageRole.USER;
              const { message, settingContent } = isUser
                ? { message: msg.content, settingContent: null }
                : parseMessageContent(msg.content);

              return (
                <div key={msg.id} className="flex flex-col gap-2">
                  <div className={`group flex gap-2.5 ${isUser ? 'justify-end' : ''}`}>
                    {!isUser && (
                      <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div className={`flex flex-col gap-1 max-w-[85%] ${isUser ? 'items-end' : ''}`}>
                      <div className={`rounded-xl px-3 py-2.5 ${isUser ? 'bg-primary/20 border border-primary' : 'bg-bg-elevated'}`}>
                        <p className={`text-[13px] leading-relaxed whitespace-pre-wrap break-words ${isUser ? 'text-primary' : 'text-gray-200'}`}>
                          {message.length > 300 ? message.slice(0, 300) + '...' : message}
                        </p>
                      </div>
                      <div className={`flex items-center gap-2 ${isUser ? 'justify-end' : ''}`}>
                        {!isUser && (
                          <button onClick={() => handleDeleteMessage(msg.id)} className="p-0.5 text-gray-500 hover:text-red-400 transition-colors">
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                        <span className={`text-[11px] ${isUser ? 'text-primary/50' : 'text-gray-400'}`}>
                          {formatTime(msg.createdAt)}
                        </span>
                        {isUser && (
                          <button onClick={() => handleDeleteMessage(msg.id)} className="p-0.5 text-gray-500 hover:text-red-400 transition-colors">
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Content Card */}
                  {!isUser && settingContent && (
                    <div className="ml-9">
                      <div className="rounded-lg border border-border-default p-3 bg-bg-surface">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[13px] font-semibold text-gray-50">{t('settingChat.generatedContent', { context: getContextLabel() })}</span>
                          <button
                            onClick={() => handleApplyContent(settingContent)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-primary text-primary transition-colors hover:bg-primary/10"
                          >
                            <CheckIcon className="w-3 h-3" />
                            {t('settingChat.apply')}
                          </button>
                        </div>
                        <div className="text-[12px] leading-relaxed max-h-[300px] overflow-y-auto text-gray-300">
                          <p className="whitespace-pre-wrap">{settingContent}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Streaming */}
            {streaming && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                  {streamingContent ? (
                    <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap break-words">{streamingContent}</p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {[0, 150, 300].map(delay => (
                          <div key={delay} className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                        ))}
                      </div>
                      <span className="text-[13px] text-gray-400">{t('subjectChat.thinking')}</span>
                    </div>
                  )}
                  {streamingContent && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                      <span className="text-[11px] text-gray-400">{t('subjectChat.generating')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex items-end gap-3 p-4 border-t border-border-subtle flex-shrink-0">
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('subjectChat.inputPlaceholder')}
            className="w-full bg-bg-elevated text-gray-50 text-[13px] rounded-lg px-3.5 py-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-gray-400 overflow-y-auto"
            rows={1}
            disabled={streaming}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!input.trim() || streaming}
          className="w-10 h-10 bg-primary hover:bg-primary/90 disabled:bg-bg-elevated disabled:text-gray-400 text-white rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
        >
          <PaperAirplaneIcon className="w-[18px] h-[18px]" />
        </button>
      </div>
    </div>
  );
}
